import React, { useEffect, useMemo, useRef, useState } from "react";
import {Route, Switch, Redirect}    from 'react-router-dom';

import "./omni-search-layout.scss";

import {OmniSearchTabs} from "./omni-search-tabs/omni-search-tabs";
import {ExploreLayout} from "./explore-layout/explore-layout";
import {BrowseLayout} from "./browse-layout/browse-layout";
import {parseUrlQuery}  from "@Common/utils/parse-url-query";
import {XdSentry} from "@Common/libs/sentry/xd-sentry";

const URL_SEARCH_NAVIGATION = "/next-api/omniSearch/search/navigation?searchTerm="
const URL_SEARCH_FREEFORM = "/next-api/omniSearch/search/freeForm?searchTerm="
const URL_SEARCH_PROGRAMS = "/next-api/omniSearch/search/programs?searchTerm="
const URL_PRODUCTS = '/next-api/omniSearch/search/products?searchTerm='
const URL_SOLR_SELECT = "/next-api/omniSearch/solr/select"
const URL_BLOGS = '/next-api/omniSearch/search/blogs?searchTerm='
const ID_COLLECTION_ORDERS = 1
const ID_COLLECTION_TEMPLATE_PAIRS = 2
const MAX_API_WAIT_EXPLORE_SECONDS_DEFAULT = 8
const MS_PER_SECOND = 1000

export const OmniSearchLayout = ({
    apis,
    cart,
    company,
    paywallModal,
    routerHistory,
    session,
    systemConstants,
    templateDownloadModal,
    userLevels
}) => {
    const initialParam = parseUrlQuery(window.location.search)
    const [queryParam, setQueryParam] = useState(initialParam?.q || "")
    const [deleteTargetOrder, setDeleteTargetOrder] = useState(null)

    const [navItems, setNavItems] = useState([])
    const [freeformItems, setFreeformItems] = useState([])
    const [programItems, setProgramItems] = useState([])
    const [products, setProducts] = useState([])
    const [orders, setOrders] = useState([])
    const [categories, setCategories] = useState([])
    const [blogPosts, setBlogPosts] = useState([])

    const [navigationLoading, setNavigationLoading] = useState(true)
    const [productsLoading, setProductsLoading] = useState(true)
    const [ordersLoading, setOrdersLoading] = useState(true)
    const [templatesLoading, setTemplatesLoading] = useState(true)
    const [blogsLoading, setBlogsLoading] = useState(true)

    const [overrideApiLoading, setOverrideApiLoading] = useState(false)

    const agentHasYearlySubscription = checkAgentYearlySubscriptionStatus({agent: session?.data?.agent})
    const agentBenefits = getAgentBenefits({agent: session?.data?.agent})
    const combinedItems = useMemo(() => {
        return [...navItems, ...freeformItems, ...programItems];
    }, [navItems, freeformItems, programItems]);

    const isApiLoading = useMemo(() => (
        navigationLoading ||
        productsLoading ||
        ordersLoading ||
        blogsLoading ||
        templatesLoading
    ), [navigationLoading, productsLoading, ordersLoading, templatesLoading, blogsLoading])

    const apiTimeoutRef = useRef(null)
    const apiWaitTime = systemConstants?.value?.MAX_API_WAIT_EXPLORE_SECONDS ?? MAX_API_WAIT_EXPLORE_SECONDS_DEFAULT

    useEffect(() => {
        const updateQueryParamFromUrl = () => {
            const params = parseUrlQuery(window.location.search)
            const updatedQueryParam = params?.q || ''
            if(updatedQueryParam !== queryParam) {
                setQueryParam(updatedQueryParam)
            }
        }

        window.addEventListener("popstate", updateQueryParamFromUrl)

        const unlisten = routerHistory?.listen?.(() => {
            updateQueryParamFromUrl()
        })

        return () => {
            window.removeEventListener("popstate", updateQueryParamFromUrl)
            if(unlisten) {
                unlisten()
            }
        }

    }, [queryParam])

    const setLoadingState = () => {
        setProducts([])
        setOrders([])
        setCategories([])
        setBlogPosts([])

        setDeleteTargetOrder(null)

        const shouldWaitForResults = queryParam ? true : false
        setOverrideApiLoading(!shouldWaitForResults)

        setNavigationLoading(shouldWaitForResults)
        setProductsLoading(shouldWaitForResults)
        setOrdersLoading(shouldWaitForResults)
        setTemplatesLoading(shouldWaitForResults)
        setBlogsLoading(shouldWaitForResults)
    }

    const bundleSentryData = () => {
        const data = []
        if(navigationLoading) {
            data.push("navigation")
        }
        if(productsLoading) {
            data.push("products")
        }
        if(ordersLoading) {
            data.push("orders")
        }
        if(templatesLoading) {
            data.push("templates")
        }
        if(blogsLoading) {
            data.push("blogs")
        }

        return data
    }

    useEffect(() => {
        setLoadingState()
            
        if(!queryParam) {
            return
        }

        apiTimeoutRef.current = setTimeout(() => {
            console.warn("Api wait limit reached")
            setOverrideApiLoading(true)

            const data = bundleSentryData()
            reportApiLimitReached(data)

        }, apiWaitTime * MS_PER_SECOND)

        Promise.all([
            queryNextApi(URL_SEARCH_NAVIGATION, queryParam),
            queryNextApi(URL_SEARCH_FREEFORM, queryParam),
            queryNextApi(URL_SEARCH_PROGRAMS, queryParam)
        ]).then(([nav, freeform, program]) => {
            setNavItems(nav)
            setFreeformItems(freeform)
            setProgramItems(program)
            setNavigationLoading(false)
        })

        queryNextApi(URL_PRODUCTS, queryParam).then((productList) => {
            if(productList) {
                setProducts(productList)
            }
            setProductsLoading(false)
        })

        querySolr(queryParam, ID_COLLECTION_ORDERS).then((ordersList) => {
            if(ordersList) {
                setOrders(ordersList)
            }
            setOrdersLoading(false)
        })

        querySolr(queryParam, ID_COLLECTION_TEMPLATE_PAIRS).then((templates) => {
            if(templates) {
                setCategories(Object.values(groupTemplates(templates)))
            }
            setTemplatesLoading(false)
        })

        queryNextApi(URL_BLOGS, queryParam).then((blogs) => {
            if(blogs) {
                setBlogPosts(blogs)
            }
            setBlogsLoading(false)
        })

        return () => clearTimeout(apiTimeoutRef.current)
    }, [queryParam])

    useEffect(() => {
        if(!isApiLoading) {
            clearTimeout(apiTimeoutRef.current)
        }
    }, [isApiLoading])

    return (
        <div className="omni-search-layout-dznts51">
            <OmniSearchTabs {...{queryParam}} />
            <Switch>
                <Route
                    exact
                    path={"/explore"}
                    render={(props) => (
                        <ExploreLayout 
                            {...props}
                            {...{
                                apis,
                                blogPosts,
                                cart,
                                categories,
                                combinedItems,
                                company,
                                deleteTargetOrder, setDeleteTargetOrder,
                                isApiLoading,
                                orders,
                                products,
                                agentHasYearlySubscription,
                                paywallModal,
                                agentBenefits,
                                templateDownloadModal,
                                queryParam,
                                routerHistory,
                                session,
                                userLevels
                            }} 
                        />
                    )}
                />
                <Route
                    exact
                    path={"/browse"}
                    render={(props) => (
                        <BrowseLayout {...{queryParam, categories, agentHasYearlySubscription, paywallModal, agentBenefits, templateDownloadModal, userLevels}} />
                    )}
                />
                
                <Redirect to={"/"}/>
            </Switch>
        </div>
    )
}

const groupTemplates = (templates, field='category_name') => {
    const groups = templates.reduce((templateGroups, template) => {
        if(!templateGroups[template[field]]) {
            templateGroups[template[field]] = []
        }
        templateGroups[template[field]].push(template)
        return templateGroups
    }, {})
    return groups
}

const queryNextApi = async (url, queryParam) => {
    try {
        const response = await fetch(`${url}${queryParam}`)
        const json = await response.json()

        if(!Array.isArray(json?.data)) {
            return []
        }
        if(json.data.length < 1) {
            return []
        }
        return json.data
    }    
    catch(error) {
        console.error(error)
    }
    return []
}

const querySolr = async (queryParam, idCollection) => {
    try {
        const response = await fetch(URL_SOLR_SELECT, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ param: queryParam, idCollection })
        })

        const data = await response.json()
        return data.response.docs ?? []
    } 
    catch(error) {
        console.error(error)
        return []
    }
}

const checkAgentYearlySubscriptionStatus = ({agent}) => {
    return agent && agent.subscriptions && agent.subscriptions[0]?.idPlanType == 2
}

const getAgentBenefits = ({agent}) => {
    return agent?.activeBenefits && agent.activeBenefits
    .map(benefit => agent.availableBenefits.indexOf(benefit))
    .filter(index => index !== -1);
}

const reportApiLimitReached = (data) => {
    XdSentry.addBreadcrumb({
        category: "omnisearch",
        message: "Explore loading API limit reached",
        level: XdSentry.Severity.Error,
        data
    })
    XdSentry.captureMessage("Explore loading API limit reached", XdSentry.Severity.Error)
}